'use client';

import { motion } from 'motion/react';
import Link from 'next/link';
import { useMemo, useRef, useState } from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import { VideoMedia, VideoMediaHandle } from '@/components/VideoMedia';
import { useLocale } from '@/contexts/LocaleContext';
import type { Media, Tech } from '@/payload-types';
import { cn } from '@/utils/cn';
import { getDictionary } from '@/utils/getDictionary';
import { pushGTMEvent } from '@/utils/pushGTMEvent';

import styles from './TechCard.module.scss';

type TechCardProps = Tech & {
  className?: string;
  disableParallax?: boolean;
  list?: boolean;
  slider?: boolean;
  tracking?: {
    type: string;
    headline: string;
    cta: string;
  };
};

export const TechCard: React.FC<TechCardProps> = (props) => {
  const { card, className, hero, logo, title, tracking, url } = props;

  const locale = useLocale();
  const t = getDictionary(locale);
  const videoRef = useRef<VideoMediaHandle>(null);
  const [motionComplete, setMotionComplete] = useState(false);

  const handleMouseEnter = () => {
    videoRef.current?.play();
  };

  const handleMouseLeave = () => {
    videoRef.current?.pause();
  };

  const MotionLink = useMemo(() => motion.create(Link), []);

  const {
    image: heroImage,
    mediaType: heroMediaType,
    videoEmbedUrl: heroVideoEmbedUrl,
    videoPoster: heroVideoPoster,
  } = hero;

  const {
    image: cardImage,
    mediaType: cardMediaType,
    override,
    title: cardTitle,
    videoEmbedUrl: cardVideoEmbedUrl,
    videoPoster: cardVideoPoster,
  } = card;

  const titleToUse = (override && cardTitle) || title;

  const MediaComponent =
    override && cardMediaType === 'image' && cardImage ? (
      <ImageMedia
        aspectRatio={'portrait'}
        image={cardImage}
        className={styles.media}
        responsiveWidths={{
          mobile: 343,
          desktop: 512,
          largeDesktop: 800,
        }}
      />
    ) : override && cardMediaType === 'video' && cardVideoEmbedUrl ? (
      <VideoMedia
        ref={videoRef}
        aspectRatio={'portrait'}
        videoPoster={cardVideoPoster as Media}
        videoEmbedUrl={cardVideoEmbedUrl}
        className={styles.media}
        videoIsBackground={true}
        videoLoops={true}
        playOnHover={true}
        delayed={!motionComplete}
      />
    ) : heroMediaType === 'image' && heroImage ? (
      <ImageMedia
        aspectRatio={'portrait'}
        image={heroImage}
        className={styles.media}
        responsiveWidths={{
          mobile: 343,
          desktop: 512,
          largeDesktop: 800,
        }}
      />
    ) : heroMediaType === 'video' && heroVideoEmbedUrl ? (
      <VideoMedia
        ref={videoRef}
        aspectRatio={'portrait'}
        videoPoster={heroVideoPoster as Media}
        videoEmbedUrl={heroVideoEmbedUrl}
        className={styles.media}
        videoIsBackground={true}
        videoLoops={true}
        playOnHover={true}
        delayed={!motionComplete}
      />
    ) : null;

  const handleClick = () => {
    if (tracking) {
      pushGTMEvent({
        event: 'component_click',
        component_cta: tracking.cta,
        component_headline: tracking.headline,
        component_type: tracking.type,
      });
    }
  };

  const LogoComponent = (logo as Media)?.url ? (
    <div
      className={styles.logo}
      style={
        {
          '--mask-image': `url(${(logo as Media).url})`,
        } as React.CSSProperties
      }
    />
  ) : null;

  return (
    <MotionLink
      href={url as string}
      className={cn(styles.card, className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      layout='position'
      style={{ willChange: 'opacity' }}
      onAnimationComplete={() => setMotionComplete(true)}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label={`${t('goTo')} ${titleToUse}`}
    >
      {LogoComponent}
      {MediaComponent}
    </MotionLink>
  );
};
